import React from 'react';
import { toApiWsUrl } from '../config/api';

export interface UseWebSocketOptions {
  /** Disable connecting (default true). */
  enabled?: boolean;
  /** Max reconnect attempts (default 5). */
  maxAttempts?: number;
  /** Async/sync function returning a short-lived token. Omit for session-based auth. */
  getToken?: () => Promise<string | null> | string | null;
  /** Extra query-string appended after the token (no leading `&` needed). */
  extraQuery?: string;
  /** Changes force the current socket to close and reconnect without altering its URL. */
  lifecycleKey?: string;
  /** Called for every message. `parsed` is the JSON parse result or null. */
  onMessage?: (event: MessageEvent, parsed: unknown) => void;
  onOpen?: (ev: Event) => void;
  onClose?: (ev: CloseEvent) => void;
  onError?: (ev: Event) => void;
}

export type WebSocketConnectionState = 'connecting' | 'reconnecting' | 'open' | 'closed' | 'error';

const TERMINAL_CLOSE_CODES = new Set([1008, 4401, 4403]);

export interface UseWebSocketReturn {
  /** Whether the WebSocket is currently open. */
  connected: boolean;
  /** Rich websocket lifecycle state for status UI. */
  connectionState: WebSocketConnectionState;
  /**
   * Send a JSON-serialisable payload through the WebSocket.
   * No-op if the socket is not open.  Returns true if sent successfully.
   */
  send: (payload: unknown) => boolean;
  /** Forcibly close + reconnect from scratch (resets backoff counter). */
  reconnect: () => void;
  /** Raw ref — prefer `send()` over direct access. */
  wsRef: React.RefObject<WebSocket | null>;
}

/**
 * Generic WebSocket hook with optional token fetch and auto-reconnect.
 *
 * `path`: absolute backend path (e.g., '/sales/api/ws').
 * The hook builds ws(s) URL from window.location automatically.
 *
 * All connection management, heartbeat pong, and exponential-backoff reconnect
 * are handled internally — consumers never need their own `new WebSocket()`.
 */
export function useWebSocket(path: string, opts: UseWebSocketOptions = {}): UseWebSocketReturn {
  const { enabled = true, maxAttempts = 5, getToken, extraQuery, lifecycleKey, onMessage, onOpen, onClose, onError } = opts;
  const wsRef = React.useRef<WebSocket | null>(null);
  const attemptsRef = React.useRef(0);
  const [connected, setConnected] = React.useState(false);
  const [connectionState, setConnectionState] = React.useState<WebSocketConnectionState>(
    enabled ? 'connecting' : 'closed',
  );
  const connectingRef = React.useRef(false);
  const cleanupTimeoutRef = React.useRef<number | null>(null);
  const reconnectTimeoutsRef = React.useRef<Set<number>>(new Set());
  const cancelledRef = React.useRef(false);

  // Stabilize callbacks to prevent effect re-runs on every render
  const onMessageRef = React.useRef(onMessage);
  const onOpenRef = React.useRef(onOpen);
  const onCloseRef = React.useRef(onClose);
  const onErrorRef = React.useRef(onError);
  const getTokenRef = React.useRef(getToken);
  const extraQueryRef = React.useRef(extraQuery);

  React.useEffect(() => {
    onMessageRef.current = onMessage;
    onOpenRef.current = onOpen;
    onCloseRef.current = onClose;
    onErrorRef.current = onError;
    getTokenRef.current = getToken;
    extraQueryRef.current = extraQuery;
  });

  // Stable connect function (does not cause re-mounts)
  const connectFn = React.useRef<(() => void) | undefined>(undefined);

  const clearReconnectTimeouts = React.useCallback(() => {
    for (const timeoutId of reconnectTimeoutsRef.current) {
      window.clearTimeout(timeoutId);
    }
    reconnectTimeoutsRef.current.clear();
  }, []);

  const scheduleReconnect = React.useCallback((callback: () => void, delay: number) => {
    const timeoutId = window.setTimeout(() => {
      reconnectTimeoutsRef.current.delete(timeoutId);
      callback();
    }, delay);
    reconnectTimeoutsRef.current.add(timeoutId);
  }, []);

  React.useEffect(() => {
    cancelledRef.current = false;

    if (!enabled) {
      // Tear down any existing connection when disabled
      cancelledRef.current = true;
      connectingRef.current = false;
      clearReconnectTimeouts();
      if (cleanupTimeoutRef.current !== null) {
        window.clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
      try { wsRef.current?.close(); } catch { /* ignore */ }
      wsRef.current = null;
      setConnected(false);
      setConnectionState('closed');
      return;
    }
    // Guard against React StrictMode double-mount or rapid re-renders
    if (connectingRef.current || wsRef.current) return;

    async function connect() {
      if (cancelledRef.current) return;
      if (connectingRef.current || wsRef.current?.readyState === WebSocket.OPEN) return;
      connectingRef.current = true;
      setConnectionState(attemptsRef.current > 0 ? 'reconnecting' : 'connecting');
      try {
        let token: string | null = null;
        const _getToken = getTokenRef.current;
        if (typeof _getToken === 'function') {
          const t = await _getToken();
          token = typeof t === 'string' ? t : null;
        } else if (typeof _getToken === 'string') {
          token = _getToken;
        }

        if (cancelledRef.current) { connectingRef.current = false; return; }

        // If token fetch failed (e.g., 429 rate limit), backoff and retry
        if (!token && typeof _getToken === 'function') {
          connectingRef.current = false;
          if (!cancelledRef.current && attemptsRef.current < maxAttempts) {
            attemptsRef.current += 1;
            setConnectionState('reconnecting');
            const delay = Math.min(5000 * Math.pow(2, attemptsRef.current - 1), 60000);
            scheduleReconnect(() => { if (!cancelledRef.current) connect(); }, delay);
          } else {
            setConnectionState('error');
          }
          return;
        }

        let qs = token ? `?token=${encodeURIComponent(token)}` : '';
        const _extra = extraQueryRef.current;
        if (_extra) {
          qs += (qs ? '&' : '?') + _extra;
        }
        const url = toApiWsUrl(`${path}${qs}`);
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = (ev) => {
          connectingRef.current = false;
          attemptsRef.current = 0;
          setConnected(true);
          setConnectionState('open');
          if (onOpenRef.current) onOpenRef.current(ev);
        };
        ws.onmessage = (msg) => {
          let parsed: unknown = null;
          try { parsed = JSON.parse(msg.data as string); } catch { /* ignore */ }

          // Auto-respond to server heartbeat (unified {"event":"ping"})
          try {
            if (
              parsed &&
              typeof parsed === 'object' &&
              (('event' in parsed && (parsed as { event: string }).event === 'ping') ||
               ('type' in parsed && (parsed as { type: string }).type === 'ping'))
            ) {
              try { ws.send(JSON.stringify({ event: 'pong' })); } catch { /* ignore */ }
              return; // don't propagate pings to consumer
            }
          } catch { /* ignore */ }

          if (onMessageRef.current) {
            try { onMessageRef.current(msg, parsed); } catch { /* swallow consumer errors */ }
          }
        };
        ws.onclose = (ev) => {
          connectingRef.current = false;
          setConnected(false);
          if (cancelledRef.current) {
            setConnectionState('closed');
          } else if (!TERMINAL_CLOSE_CODES.has(ev.code) && attemptsRef.current < maxAttempts) {
            setConnectionState('reconnecting');
          } else {
            setConnectionState(ev.code === 1000 ? 'closed' : 'error');
          }
          if (wsRef.current === ws) wsRef.current = null;
          if (onCloseRef.current) onCloseRef.current(ev);
          if (!cancelledRef.current && !TERMINAL_CLOSE_CODES.has(ev.code) && attemptsRef.current < maxAttempts) {
            attemptsRef.current += 1;
            const delay = Math.min(1000 * Math.pow(2, attemptsRef.current - 1), 10000);
            scheduleReconnect(() => { if (!cancelledRef.current) connect(); }, delay);
          }
        };
        ws.onerror = (ev) => {
          connectingRef.current = false;
          setConnectionState('error');
          if (onErrorRef.current) onErrorRef.current(ev);
          try { ws.close(); } catch { /* ignore */ }
        };
      } catch {
        connectingRef.current = false;
        if (!cancelledRef.current && attemptsRef.current < maxAttempts) {
          attemptsRef.current += 1;
          setConnectionState('reconnecting');
          const delay = Math.min(1000 * Math.pow(2, attemptsRef.current - 1), 10000);
          scheduleReconnect(() => { if (!cancelledRef.current) connect(); }, delay);
        } else {
          setConnectionState('error');
        }
      }
    }

    connectFn.current = connect;

    // Delay initial connection slightly to avoid hot-reload storms
    cleanupTimeoutRef.current = window.setTimeout(() => {
      if (cancelledRef.current) return;
      connect();
    }, 100);

    return () => {
      cancelledRef.current = true;
      connectingRef.current = false;
      if (cleanupTimeoutRef.current !== null) {
        window.clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
      clearReconnectTimeouts();
      const ws = wsRef.current;
      if (ws) {
        try {
          // Avoid noisy browser warning "closed before connection is established"
          // by not closing while CONNECTING synchronously in cleanup.
          if (ws.readyState === WebSocket.OPEN) {
            ws.close(1000, 'cleanup');
          } else if (ws.readyState === WebSocket.CONNECTING) {
            ws.addEventListener(
              'open',
              () => {
                try { ws.close(1000, 'cleanup'); } catch { /* ignore */ }
              },
              { once: true },
            );
          }
        } catch {
          /* ignore */
        }
      }
      wsRef.current = null;
      setConnectionState('closed');
    };
  }, [enabled, path, maxAttempts, lifecycleKey, clearReconnectTimeouts, scheduleReconnect]);

  // ── send() ──────────────────────────────────────────────────
  const send = React.useCallback((payload: unknown): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(typeof payload === 'string' ? payload : JSON.stringify(payload));
      return true;
    } catch { return false; }
  }, []);

  // ── reconnect() ─────────────────────────────────────────────
  const reconnect = React.useCallback(() => {
    // Prevent the closing socket's onclose from triggering a competing auto-reconnect
    cancelledRef.current = true;
    clearReconnectTimeouts();
    attemptsRef.current = 0;
    connectingRef.current = false;
    const ws = wsRef.current;
    if (ws) {
      try {
        if (ws.readyState === WebSocket.OPEN) {
          ws.close(1000, 'reconnect');
        } else if (ws.readyState === WebSocket.CONNECTING) {
          ws.addEventListener(
            'open',
            () => {
              try { ws.close(1000, 'reconnect'); } catch { /* ignore */ }
            },
            { once: true },
          );
        }
      } catch {
        /* ignore */
      }
    }
    wsRef.current = null;
    setConnected(false);
    setConnectionState('connecting');
    // Re-enable and connect fresh after close propagates
    scheduleReconnect(() => {
      cancelledRef.current = false;
      connectFn.current?.();
    }, 50);
  }, [clearReconnectTimeouts, scheduleReconnect]);

  return { connected, connectionState, send, reconnect, wsRef };
}

export default useWebSocket;
